import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Star, Compass, CreditCard, HandCoins } from "lucide-react";
import PayPalCheckout, { usePaymentsConfig } from "@/components/PayPalCheckout";

const coachingOptions = [
  { name: "Per Session", price: "$200", note: "Pay as you go, one session at a time", tierValue: "Career Coaching — Per Session ($200)", payKey: "coaching_session" },
  { name: "Signature Program", price: "$4,989", note: "5 sessions — the complete path, end to end", monthlyHint: "or split monthly — from $415.75/mo", featured: true, tierValue: "Career Coaching — 5-Session Signature Program ($4,989)", payKey: "coaching_signature" },
  { name: "Custom Plan", price: "Custom", note: "Priced after your free consultation", tierValue: "Career Coaching — Custom Plan" },
];

const coachingFeatures = [
  "Resume & LinkedIn overhaul for the US market",
  "Credential positioning (CMA / CPA pathways)",
  "Interview preparation & mock interviews",
  "US job-market strategy & networking playbook",
  "Salary negotiation guidance",
  "Ongoing mentorship check-ins",
];

const tiers = [
  {
    name: "Essential",
    price: "$150",
    prefix: "Starting at",
    tagline: "Simple tax preparation & consulting",
    features: [
      "Simple individual (Form 1040) tax return",
      "One-on-one consulting session",
      "E-file with direct-deposit refund setup",
      "Prior-year return review",
      "Document checklist & secure upload",
    ],
    cta: "Start simple",
    payKey: "essential",
    payPrice: "$150",
    payLabel: "Pay $150 now",
    payNote: "Simple individual (Form 1040) return plus a one-on-one consulting session.",
  },
  {
    name: "Professional",
    price: "$200–$500",
    prefix: "Per service",
    tagline: "The right expert for each job",
    highlight: true,
    features: [
      "Complex individual & business tax returns",
      "Monthly bookkeeping & reconciliations",
      "QuickBooks setup, cleanup & training",
      "Tax planning & estimated payments",
      "Career coaching sessions",
      "Nonprofit grant & fund accounting support",
    ],
    cta: "Pick your service",
    payKey: "professional",
    payPrice: "$200",
    payLabel: "Pay $200 to start",
    payNote: "Opening payment for a $200–$500 engagement. The balance is quoted in writing before any further work begins.",
  },
  {
    name: "Comprehensive",
    price: "$4,979",
    prefix: "Full-service package",
    monthlyHint: "or split into 3, 6, or 12 monthly payments — from $415/mo",
    tagline: "Your finance department, handled",
    features: [
      "Year-round bookkeeping, tax & filing",
      "Fractional CFO / controller guidance",
      "Monthly financial statements & insights",
      "Audit-ready books & compliance support",
      "Quarterly strategy reviews",
      "Priority response, all year long",
    ],
    cta: "Go all-in",
    payKey: "comprehensive",
    payPrice: "$4,979",
    payLabel: "Pay $4,979 — or monthly",
    payNote: "Year-round bookkeeping, tax, filing and fractional CFO guidance.",
  },
];

export default function Pricing() {
  const payments = usePaymentsConfig();
  const [checkout, setCheckout] = useState(null);
  const canPay = !!payments?.configured;

  return (
    <section id="pricing" data-testid="pricing-section" className="border-t border-linen">
      <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-terra"
        >
          Transparent Pricing
        </motion.p>
        <div className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="max-w-2xl font-serif text-4xl font-medium leading-tight tracking-tight text-pine md:text-5xl"
            data-testid="pricing-heading"
          >
            Clear numbers. <span className="italic text-forest">No surprises.</span>
          </motion.h2>
          <p className="max-w-sm text-sm leading-relaxed text-pine/60">
            Every engagement begins with a free 30-minute consultation and a fixed quote you
            approve before any work starts.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {tiers.map((t, i) => (
            <motion.article
              key={t.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.76, 0, 0.24, 1] }}
              className={`relative flex flex-col p-9 md:p-10 ${
                t.highlight
                  ? "border-2 border-forest bg-forest text-bone lg:-my-4 lg:py-14"
                  : "border border-linen bg-white/70 text-pine"
              }`}
              data-testid={`pricing-tier-${t.name.toLowerCase()}`}
            >
              {t.highlight && (
                <span className="absolute -top-3.5 left-9 inline-flex items-center gap-1.5 bg-terra px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-bone">
                  <Star size={10} fill="currentColor" /> Most popular
                </span>
              )}
              <p className={`mb-2 font-mono text-[10px] uppercase tracking-[0.25em] ${t.highlight ? "text-bone/60" : "text-terra"}`}>
                {t.name}
              </p>
              <p className={`mb-1 font-mono text-[10px] uppercase tracking-[0.2em] ${t.highlight ? "text-bone/50" : "text-pine/50"}`}>
                {t.prefix}
              </p>
              <p className="mb-3 font-serif text-5xl font-semibold tracking-tight" data-testid={`pricing-price-${t.name.toLowerCase()}`}>
                {t.price}
              </p>
              {t.monthlyHint && (
                <p className={`-mt-1 mb-3 font-mono text-[10px] uppercase tracking-[0.15em] ${t.highlight ? "text-bone/50" : "text-terra"}`} data-testid={`pricing-monthly-hint-${t.name.toLowerCase()}`}>
                  {t.monthlyHint}
                </p>
              )}
              <p className={`mb-8 font-serif text-lg italic ${t.highlight ? "text-bone/80" : "text-forest"}`}>{t.tagline}</p>
              <ul className="mb-10 flex flex-1 flex-col gap-3.5">
                {t.features.map((f) => (
                  <li key={f} className={`flex items-start gap-3 text-sm leading-snug ${t.highlight ? "text-bone/85" : "text-pine/75"}`}>
                    <Check size={15} className={`mt-0.5 shrink-0 ${t.highlight ? "text-terra" : "text-forest"}`} />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="#booking"
                onClick={() => {
                  const val = `${t.name} (${t.price})`;
                  sessionStorage.setItem("bc_selected_tier", val);
                  window.dispatchEvent(new CustomEvent("bc-tier-selected", { detail: val }));
                }}
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                className={`inline-flex items-center justify-center gap-2 border px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                  t.highlight
                    ? "border-bone bg-bone text-forest hover:bg-transparent hover:text-bone"
                    : "border-forest bg-forest text-bone hover:bg-transparent hover:text-forest"
                }`}
              >
                {t.cta} <ArrowRight size={15} />
              </a>
              {canPay && t.payKey && (
                <button
                  type="button"
                  onClick={() =>
                    setCheckout({
                      packageId: t.payKey,
                      label: `${t.name} — ${t.tagline}`,
                      price: t.payPrice,
                      note: t.payNote,
                    })
                  }
                  data-testid={`pricing-pay-${t.name.toLowerCase()}`}
                  className={`mt-3 inline-flex items-center justify-center gap-2 border px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    t.highlight
                      ? "border-bone/40 text-bone/80 hover:border-bone hover:text-bone"
                      : "border-pine/25 text-pine/70 hover:border-forest hover:text-forest"
                  }`}
                >
                  <CreditCard size={14} /> {t.payLabel}
                </button>
              )}
            </motion.article>
          ))}
        </div>

        <motion.article
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          className="mt-10 scroll-mt-24 border-2 border-pine bg-pine p-9 text-bone md:p-12"
          id="coaching-pricing"
          data-testid="pricing-tier-coaching"
        >
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-terra">
                <Compass size={14} /> Career Coaching
              </p>
              <h3 className="mb-4 font-serif text-3xl font-semibold leading-tight tracking-tight md:text-4xl">
                Your path into US finance, <span className="italic text-terra">mapped</span>.
              </h3>
              <p className="mb-8 text-sm leading-relaxed text-bone/70">
                Dedicated one-on-one coaching for immigrant and internationally trained
                professionals — from a mentor who has walked the road and guided others to
                CFO-level careers.
              </p>
              <ul className="flex flex-col gap-3">
                {coachingFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm leading-snug text-bone/85">
                    <Check size={15} className="mt-0.5 shrink-0 text-terra" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col justify-center gap-4 lg:col-span-7">
              {coachingOptions.map((o) => (
                <a
                  key={o.name}
                  href="#booking"
                  onClick={() => {
                    sessionStorage.setItem("bc_selected_tier", o.tierValue);
                    window.dispatchEvent(new CustomEvent("bc-tier-selected", { detail: o.tierValue }));
                  }}
                  data-testid={`coaching-option-${o.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`group flex items-center justify-between gap-6 border p-6 transition-colors duration-300 ${
                    o.featured
                      ? "border-terra bg-terra/10 hover:bg-terra/20"
                      : "border-bone/15 hover:border-bone/40"
                  }`}
                >
                  <div>
                    <p className="mb-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">
                      {o.name}
                      {o.featured && (
                        <span className="inline-flex items-center gap-1 bg-terra px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.2em] text-bone">
                          <Star size={8} fill="currentColor" /> Best value
                        </span>
                      )}
                    </p>
                    <p className="font-serif text-3xl font-semibold tracking-tight">{o.price}</p>
                    <p className="mt-1 text-xs text-bone/60">{o.note}</p>
                    {o.monthlyHint && (
                      <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.15em] text-terra" data-testid="coaching-monthly-hint">
                        {o.monthlyHint}
                      </p>
                    )}
                    {canPay && o.payKey && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCheckout({
                            packageId: o.payKey,
                            label: `Career Coaching — ${o.name}`,
                            price: o.price,
                            note: o.note,
                          });
                        }}
                        data-testid={`coaching-pay-${o.name.toLowerCase().replace(/\s+/g, "-")}`}
                        className="mt-4 inline-flex items-center gap-2 border border-bone/25 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/70 transition-colors duration-300 hover:border-terra hover:text-terra"
                      >
                        <CreditCard size={13} /> Pay {o.price} {o.monthlyHint ? "— or monthly" : "now"}
                      </button>
                    )}
                  </div>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-bone/20 text-bone/60 transition-all duration-300 group-hover:border-terra group-hover:bg-terra group-hover:text-bone">
                    <ArrowRight size={16} />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </motion.article>

        <motion.article
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          className="mt-6 border border-forest bg-forest p-9 text-bone md:p-12"
          id="strategy-pricing"
          data-testid="pricing-tier-strategy"
        >
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-xl">
              <p className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                <HandCoins size={14} /> Financial Solution Strategies
              </p>
              <h3 className="mb-4 font-serif text-3xl font-semibold leading-tight tracking-tight md:text-4xl">
                A money game plan for <span className="italic text-gold">your family</span>.
              </h3>
              <p className="text-sm leading-relaxed text-bone/70">
                One 60-minute strategy session for individual families and small business owners —
                budgeting that sticks, debt payoff and savings roadmaps, retirement planning,
                children's education savings, and cash-flow strategy.
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-start gap-4 lg:items-end">
              <p className="font-serif text-5xl font-semibold tracking-tight" data-testid="strategy-price">
                $150<span className="text-base font-normal text-bone/60"> / session</span>
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <a
                  href="#booking"
                  onClick={() => {
                    sessionStorage.setItem("bc_selected_tier", "Financial Solution Strategy — Session ($150)");
                    window.dispatchEvent(new CustomEvent("bc-tier-selected", { detail: "Financial Solution Strategy — Session ($150)" }));
                  }}
                  data-testid="strategy-book-btn"
                  className="inline-flex items-center justify-center gap-2 border border-gold bg-gold px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-pine transition-colors duration-300 hover:bg-transparent hover:text-gold"
                >
                  Book a session <ArrowRight size={14} />
                </a>
                {canPay && (
                  <button
                    type="button"
                    onClick={() =>
                      setCheckout({
                        packageId: "strategy_session",
                        label: "Financial Solution Strategy — Session",
                        price: "$150",
                        note: "One 60-minute strategy session — budgeting, debt payoff, retirement and education savings, cash flow.",
                      })
                    }
                    data-testid="strategy-pay-btn"
                    className="inline-flex items-center justify-center gap-2 border border-bone/30 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-bone/80 transition-colors duration-300 hover:border-bone hover:text-bone"
                  >
                    <CreditCard size={14} /> Pay $150 now
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.article>

        <p className="mt-12 max-w-2xl font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-pine/40" data-testid="pricing-note">
          Final pricing depends on complexity and is confirmed in writing after your free
          consultation — you always approve before work begins.
        </p>
      </div>

      {checkout && (
        <PayPalCheckout {...checkout} config={payments} onClose={() => setCheckout(null)} />
      )}
    </section>
  );
}
